import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { alertSources } from 'features/Weather/features/WeatherAlerts/config/alertSources.config';
import { alertTypeGroups } from 'features/Weather/features/WeatherAlerts/config/alertTypes.config';
import { useState } from 'react';
import { BsChevronDown, BsChevronRight } from 'react-icons/bs';
import { components as selectComponents } from 'react-select';

const EMPTY_ALERT_TYPES = [];

const CollapsibleAlertTypeGroupHeading = (props) => {
  const isCollapsed = props.isCollapsed ?? false;
  const isSearching = Boolean(props.selectProps.inputValue);

  return (
    <button
      type="button"
      className="d-flex w-100 align-items-center justify-content-between border-0 bg-transparent px-2 py-2 text-start text-theme fw-semibold"
      aria-expanded={isSearching || !isCollapsed}
      aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${String(props.data.label)}`}
      disabled={isSearching}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        props.toggle?.();
      }}
    >
      <span>{props.children}</span>
      {isCollapsed ? <BsChevronRight aria-hidden="true" /> : <BsChevronDown aria-hidden="true" />}
    </button>
  );
};

const CollapsibleAlertTypeGroup = (props) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isSearching = Boolean(props.selectProps.inputValue);

  return (
    <selectComponents.Group
      {...props}
      headingProps={{
        ...props.headingProps,
        isCollapsed,
        toggle: () => setIsCollapsed((collapsed) => !collapsed),
      }}
      Heading={CollapsibleAlertTypeGroupHeading}
    >
      {isCollapsed && !isSearching ? null : props.children}
    </selectComponents.Group>
  );
};

/**
 * Renders the searchable, grouped Alert List type filter.
 *
 * @param {{selectedSources?: string[], selectedTypesBySource?: Record<string, string[]>, onChange: (typesBySource: Record<string, string[]>) => void}} props - Selected sources, selected types, and their update callback.
 * @returns {JSX.Element} The searchable alert-type select control.
 */
const AlertTypeFilter = ({ selectedSources = [], selectedTypesBySource = {}, onChange }) => {
  // alert type options filtered by currently selected sources
  const alertTypeOptions = alertTypeGroups.filter(
    ({ sourceId }) => selectedSources.length === 0 || selectedSources.includes(sourceId),
  );
  const selectedAlertTypeOptions = alertTypeOptions
    .flatMap(({ options }) => options)
    .filter(({ sourceId, typeValue }) =>
      (selectedTypesBySource[sourceId] ?? EMPTY_ALERT_TYPES).includes(typeValue),
    );

  const handleChange = (options) => {
    const nextTypesBySource = Object.fromEntries(alertSources.map(({ id }) => [id, []]));

    (options ?? []).forEach(({ sourceId, typeValue }) => {
      nextTypesBySource[sourceId]?.push(typeValue);
    });

    onChange(nextTypesBySource);
  };

  return (
    <div style={{ width: 'clamp(220px, 25vw, 320px)', flexShrink: 0 }}>
      <BaseSelect
        inputId="weather-alert-types-filter"
        wrapperClassName="w-100"
        isMulti
        isSearchable
        isClearable
        maxMenuHeight={320}
        classNames={{
          control: () => 'bg-primary',
        }}
        selectProps={{
          'aria-label': 'Alert types',
          components: { Group: CollapsibleAlertTypeGroup },
          options: alertTypeOptions,
          value: selectedAlertTypeOptions,
          formatOptionLabel: (option, { context }) => {
            if (context !== 'value') return option.label;

            const sourceLabel = alertSources.find(({ id }) => id === option.sourceId)?.label;
            return sourceLabel ? `${sourceLabel}: ${option.label}` : option.label;
          },
          placeholder: 'Alert Types Filter',
          onChange: handleChange,
        }}
      />
    </div>
  );
};

export default AlertTypeFilter;
